import { VariantProps } from 'class-variance-authority';
import { ComponentProps, useCallback, useEffect, useMemo, useState } from 'react';

import { mergeClasses } from '@/utils';

import { TABLE_HEADER_VARIANTS } from './Table.constants';
import { ITableContextValue, ITableRowData, TableSelection } from './interfaces';
import TableContext from './TableContext';
import { isRowSelected } from './Table.utils';

interface IBaseTable extends ComponentProps<'table'>, VariantProps<typeof TABLE_HEADER_VARIANTS> {
  template?: 'normal' | 'zebra';
  totalServerSelection?: number;
  includes?: TableSelection;
  excludes?: TableSelection;
  onSelectionChange?: (
    includes: TableSelection,
    excludes: TableSelection,
    selectedRowsCount: number,
  ) => void;
}

/**
 * requires some fields when hasCheckBox is true.
 */
type ITable =
  | (IBaseTable & {
      hasCheckBox: true;
      rows: ITableRowData[];
    })
  | (IBaseTable & {
      hasCheckBox?: false;
      rows?: ITableRowData[];
    });

const Table = ({
  className,
  size = 'default',
  hasCheckBox = false,
  template = 'normal',
  rows = [],
  totalServerSelection = 0,
  includes: controlledIncludes = new Set(),
  excludes: controlledExcludes = new Set(),
  onSelectionChange,
  ...props
}: ITable) => {
  const [includes, setIncludes] = useState<TableSelection>(controlledIncludes);
  const [excludes, setExcludes] = useState<TableSelection>(controlledExcludes);
  const isAllSelected = useMemo(
    () => rows.every((row) => isRowSelected(row, includes, excludes)),
    [rows, includes, excludes],
  );
  const isIndeterminate = useMemo(
    () => rows.some((row) => isRowSelected(row, includes, excludes)) && !isAllSelected,
    [rows, includes, excludes, isAllSelected],
  );
  const selectedRowsCount = useMemo(
    () => totalServerSelection + includes.size - excludes.size,
    [totalServerSelection, includes, excludes],
  );

  const processSelection = useCallback(
    (rows: ITableRowData[], isChecking: boolean) => {
      const newIncludes = new Set(includes);
      const newExcludes = new Set(excludes);

      for (const row of rows) {
        if (!row.id) continue;
        // Is checking:
        // - Delete any server deselection
        // - Add any client unselection
        if (isChecking) {
          if (row.isSelected) {
            newExcludes.delete(row.id);
          } else {
            newIncludes.add(row.id);
          }
        }
        // Is unchecking:
        // - Exclude any server selection
        // - Delete any client selection
        else {
          if (row.isSelected) {
            newExcludes.add(row.id);
          } else {
            newIncludes.delete(row.id);
          }
        }
      }
      setIncludes(newIncludes);
      setExcludes(newExcludes);
    },
    [includes, excludes],
  );

  const onSelectAll = () => {
    processSelection(rows, !isAllSelected);
  };

  const onSelectRow = useCallback(
    (rowId: string | number, isChecking: boolean) => {
      const row = rows.find((row) => row.id === rowId);
      if (!row) return;

      processSelection([row], isChecking);
    },
    [processSelection, rows],
  );

  const contextValue: ITableContextValue = {
    template,
    rows,
    size,
    hasCheckBox,
    checkboxState: {
      includes,
      excludes,
      isAllSelected,
      isIndeterminate,
    },
    onSelectAll: onSelectAll,
    onSelectRow: onSelectRow,
  };

  useEffect(() => {
    onSelectionChange?.(includes, excludes, selectedRowsCount);
  }, [includes, excludes, selectedRowsCount, onSelectionChange]);

  return (
    <TableContext.Provider value={contextValue}>
      <div
        data-slot="table-container"
        className={mergeClasses(
          'relative w-full overflow-x-auto border border-neutral-4 rounded-xs',
          className,
        )}
      >
        <table
          data-slot="table"
          className="w-full caption-bottom border-separate border-spacing-0 [&_tr:last-of-type_td]:border-none"
          {...props}
        />
      </div>
    </TableContext.Provider>
  );
};

export default Table;
